import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { Button, TextInput, Checkbox } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconArrowRight } from "@tabler/icons-react";
import { registerUser } from "../../../api";
import { BarRegistrationInput } from "./FormComponents";
import { nonProtectedRoutes } from "../../../utils/nonProtectedRoutingPaths";

export const RegisterForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState({
    success: false,
    message: "",
  });
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
      first_name: "",
      last_name: "",
      bar_jurisdiction: "",
      bar_registration_number: "",
      terms_accepted: false,
      attorney_confirmation: false,
      agree_to_marketing: false,
    },
    validate: {
      email: (value) => (!/\S+@\S+\.\S+/.test(value) ? "Invalid email" : null),
      password: (value) =>
        value.length < 8 ? "Password must be at least 8 characters" : null,
      first_name: (value) => (value.trim() ? null : "First name is required"),
      last_name: (value) => (value.trim() ? null : "Last name is required"),
      terms_accepted: (value) =>
        value ? null : "You must agree to the Terms of Service",
      attorney_confirmation: (value) =>
        value
          ? null
          : "You must confirm your status as a licensed attorney or working under direct supervision",
    },
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitResult({ success: false, message: "" });

    try {
      const response = await registerUser(values);

      if (response.data && response.data.access_token) {
        localStorage.setItem("access_token", response.data.access_token);
        navigate("/app/dashboard");
      }
    } catch (error) {
      console.error("Registration error:", error);

      if (error.response && error.response.data) {
        const fieldErrors = error.response.data;

        const formattedErrors = {};
        Object.keys(fieldErrors).forEach((key) => {
          formattedErrors[key] = Array.isArray(fieldErrors[key])
            ? fieldErrors[key][0]
            : fieldErrors[key];
        });

        form.setErrors(formattedErrors);

        setSubmitResult({
          success: false,
          message: "Please correct the errors in the form.",
        });
      } else if (error.request) {
        setSubmitResult({
          success: false,
          message:
            "No response from server. Please check your connection and try again.",
        });
      } else {
        setSubmitResult({
          success: false,
          message: "An error occurred. Please try again later.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6 my-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Register</h2>

      {submitResult.message && (
        <div
          className={`p-4 mb-6 rounded-md ${submitResult.success ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}
        >
          {submitResult.message}
        </div>
      )}

      <form onSubmit={form.onSubmit(handleSubmit)} className="space-y-4">
        <TextInput
          label="Email"
          placeholder="Enter your email"
          required
          {...form.getInputProps("email")}
          className="w-full"
          error={form.errors.email}
        />

        <TextInput
          label="Password"
          type="password"
          placeholder="Enter your password"
          required
          {...form.getInputProps("password")}
          className="w-full"
          error={form.errors.password}
        />

        <div className="grid grid-cols-2 gap-4">
          <TextInput
            label="First Name"
            placeholder="Enter first name"
            required
            {...form.getInputProps("first_name")}
            className="w-full"
            error={form.errors.first_name}
          />

          <TextInput
            label="Last Name"
            placeholder="Enter last name"
            required
            {...form.getInputProps("last_name")}
            className="w-full"
            error={form.errors.last_name}
          />
        </div>

        <TextInput
          label="Bar Jurisdiction"
          placeholder="Enter your bar jurisdiction"
          {...form.getInputProps("bar_jurisdiction")}
          className="w-full"
        />

        <BarRegistrationInput form={form} />

        <Checkbox
          label={
            <span>
              I agree to the &nbsp;
              <a
                href={nonProtectedRoutes.TERMS}
                className="text-blue-600 hover:underline"
                target="_blank"
              >
                Terms of Service
              </a>
            </span>
          }
          {...form.getInputProps("terms_accepted", { type: "checkbox" })}
          error={form.errors.terms_accepted}
          className="mt-4"
        />

        <Checkbox
          label="I confirm that I am a licensed attorney or work under the direct supervision of a licensed attorney"
          {...form.getInputProps("attorney_confirmation", { type: "checkbox" })}
          error={form.errors.attorney_confirmation}
          className="mt-4"
        />
        <Checkbox
          label="I agree to receive marketing emails from Socius Legal"
          {...form.getInputProps("agree_to_marketing", { type: "checkbox" })}
          className="mt-4"
        />

        <div className="pt-4">
          <Button
            type="submit"
            disabled={isSubmitting}
            fz="1.1rem"
            size="md"
            fullWidth
            rightSection={<IconArrowRight size={18} />}
            style={{
              backgroundColor: "var(--accent-gold)",
              transition: "background-color 0.3s ease-in-out",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                "var(--accent-gold-hover)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--accent-gold)")
            }
          >
            {isSubmitting ? "Registering..." : "Register"}
          </Button>
          <a
            href={nonProtectedRoutes.LOGIN}
            className="block w-full mt-3 py-2 text-center text-sm font-medium border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Already have a Socius account? <span className="font-semibold">Login Here</span>
          </a>
        </div>
      </form>
    </div>
  );
};
